/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import ResizeObserver from 'react-resize-observer';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import moment from 'moment';
import HeaderDropdownMenu from './HeaderDropdownMenu';
import CalendarNameTooltip from './CalendarNameTooltip';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import {
  setHeaderHeight, setSelectedDate, setSelectedProgramDayNumber, setActiveModal, setSelectedWeekViewDayToggle,
  initMonthViewWindow, requestMonthViewMonthNav,
} from '../../ducks/calendarActions';
import BreadCrumbs from './BreadCrumbs';
import GearIconButtons from './GearIconButtons';
import CloudUploadIcon from './CloudUploadIcon';
import usePreSaveNavigationGuard from '../../hooks/usePreSaveNavigationGuard';
import MobileSideBarToggle from './MobileSideBarToggle';
import useIsTextTruncated from '../../hooks/useIsTextTruncated';
import useCalendarArrowNavigation from '../../hooks/useCalendarArrowNavigation';

const MenuWrapper = styled('div')`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  @media screen and (max-width: 773px) {
    gap: 10px;
    flex-shrink: 0;
    flex-wrap: nowrap;
  }
  @media screen and (max-width: 570px) {
    gap: 8px;
  }
`;

const HeaderTextWrapper = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
`;

const NameDiv = styled('div')`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  min-width: 0;
  max-width: 100%;
  transition: ${TransitionGlobal};
`;

const NameText = styled('span')`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 22px;
  font-weight: 700;
  color: #444444;
  @media screen and (max-width: 773px) {
    font-size: 18px;
  }
  @media screen and (max-width: 570px) {
    font-size: 16px;
  }
`;

const HeaderContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 8px;
  flex-wrap: wrap;
  gap: 20px;
  border-bottom: 1px solid #EEEEEE;
  background-color: ${(props) => props.bgColor};
  ${(props) => props.viewType === 'weekView' && 'position: fixed;'}
  z-index: 99;
  @media screen and (max-width: 773px) {
    flex-wrap: nowrap;
    gap: 12px;
  }
  @media screen and (max-width: 570px) {
    gap: 8px;
  }
`;

const NavArrowButton = styled('div')`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border-radius: 50%;
  padding: 0;
  width: 32px;
  height: 32px;
  transition: all 250ms ease-in-out;
  position: relative;
  
  &:hover {
    background-color: rgba(238, 238, 238, 0.6);
  }
  
  &:active {
    background-color: rgba(238, 238, 238, 0.9);
    transition: background-color 100ms ease-in-out;
  }
  
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const HeaderAndDropdownWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 32px;
  flex: 1 1 auto;
  min-width: 0;
  @media screen and (max-width: 773px) {
    flex-shrink: 1;
    overflow: hidden;
    gap: 8px;
  }
`;

const BreadCrumbsAndNameWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  min-width: 0;
  @media screen and (max-width: 773px) {
    overflow: hidden;
  }
`;

const TodayButton = styled('div')`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #EEEEEE;
  border-radius: 24px;
  min-height: 35px;
  height: 35px;
  min-width: 85px;
  box-shadow: 0px 1px 3px 0px #3031331A;
  padding: 4px 16px;
  transition: all 250ms ease-in-out;
  user-select: none;

  &:hover {
    background-color: rgba(238, 238, 238, 0.6);
  }

  &:active {
    background-color: rgba(238, 238, 238, 0.9);
    transition: background-color 100ms ease-in-out;
  }

  @media screen and (max-width: 773px) {
    min-height: 30px;
    height: 30px;
    min-width: unset;
    padding: 3px 12px;
    span, p {
      font-size: 14px !important;
    }
  }

  @media screen and (max-width: 570px) {
    min-height: 28px;
    height: 28px;
    padding: 2px 10px;
    span, p {
      font-size: 13px !important;
    }
  }
`;

const DaySelectBox = styled('div')`
  width: 174px; /* Reduced from 232px by 25% */
  @media screen and (max-width: 773px) {
    width: 120px;
  }
  @media screen and (max-width: 570px) {
    width: 80px;
  }
`;

const MobileHidden = styled('div')`
  display: contents;
  @media screen and (max-width: 570px) {
    display: none;
  }
`;

const TabletHidden = styled('div')`
  display: contents;
  @media screen and (max-width: 773px) {
    display: none;
  }
`;

const CalendarNameWrapper = styled('div')`
  min-width: 0;
`;

const HeaderComponent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const nonDateBasedProgramItems = useSelector((state) => state.calendar.data.nonDateBasedProgramItems);
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );

  const guardNavigation = usePreSaveNavigationGuard();

  const [showDropdown, setShowDropdown] = useState(false);
  const hasRun = useRef(false);

  /** this funtion will run once on mount in weekview
   * this will set the start of the week to sunday by default
   * on inital load
   * after that the user selected date will always be the start of the week
   */
  useEffect(() => {
    if (
      !hasRun.current
    && viewType === 'weekView'
    && selectedDate
    && !moment(selectedDate).isSame(moment(selectedDate).startOf('week'), 'day')
    ) {
      dispatch(setSelectedDate(moment(selectedDate).startOf('week')));
      hasRun.current = true;
    }
  }, [viewType, selectedDate, dispatch]);

  /** Function to add a day/week to the selected date */
  const addDay = () => guardNavigation(() => {
    if (viewType === 'monthView') {
      // Jump to the next month relative to what's scrolled into view. MonthView
      // owns the scroll DOM, so it resolves the visible month and recenters.
      dispatch(requestMonthViewMonthNav('next'));
      return;
    }
    if (viewType === 'weekView') {
      if (programType === 'free-form-program') {
        if (groupByFilter === 'Day') {
          const itemsArray = Object.values(nonDateBasedProgramItems);

          const currentItem = nonDateBasedProgramItems[selectedProgramDayNumber];
          const currentDayInWeek = currentItem?.dayInWeek;

          /** If it's already day 7, stop — no next day exists */
          if (currentDayInWeek === 7) {
            return;
          }

          const nextDayInWeek = currentDayInWeek + 1;

          /** Find all items matching the next day */
          const matchingItems = itemsArray.filter((item) => item.dayInWeek === nextDayInWeek);

          if (matchingItems.length === 0) {
            return;
          }

          /** Pick the one with the lowest weekNumber */
          const nextItem = matchingItems.reduce((min, item) => (item.weekNumber < min.weekNumber ? item : min));

          dispatch(setSelectedProgramDayNumber(nextItem.dayNumber));
        }

        if (groupByFilter === 'Week') {
          const currentWeekNumber = nonDateBasedProgramItems[selectedProgramDayNumber]?.weekNumber;
          const targetWeek = currentWeekNumber + 1;

          /** Get dayNumber of the first day in the next week */
          const dayNumberOfNextWeekStart = Object.values(nonDateBasedProgramItems).find(
            (item) => item.weekNumber === targetWeek,
          )?.dayNumber;

          if (currentWeekNumber && dayNumberOfNextWeekStart) {
            dispatch(setSelectedProgramDayNumber(dayNumberOfNextWeekStart));
          }
        }
      }
      if (programType !== 'free-form-program') {
      /** For week view (Everyday or a weekday toggle), slide forward one week */
        const nextWeekStart = moment(selectedDate)
          .add(7, 'days')
          .toDate();
        dispatch(setSelectedDate(nextWeekStart));
      }
    } else {
    /** For program library day view, add 1 index and get dayNumber at index until hit last day of program */
      const sortedDayNumbers = Object.keys(nonDateBasedProgramItems)
        .map(Number)
        .sort((a, b) => a - b);
      const currentIndex = sortedDayNumbers.indexOf(selectedProgramDayNumber);
      const nextSelectedProgramDayNumber = sortedDayNumbers[currentIndex + 1];

      if (
        programType === 'free-form-program'
      && nextSelectedProgramDayNumber !== undefined
      ) {
        dispatch(setSelectedProgramDayNumber(nextSelectedProgramDayNumber));
      }

      /** For calendar day view, add 1 day */
      if (programType !== 'free-form-program') {
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        dispatch(setSelectedDate(nextDay));
      }
    }
  });

  /** Function to subtract a day/week from the selected date */
  const subtractDay = () => guardNavigation(() => {
    if (viewType === 'monthView') {
      // Jump to the previous month relative to what's scrolled into view.
      dispatch(requestMonthViewMonthNav('prev'));
      return;
    }
    if (viewType === 'weekView') {
      if (programType === 'free-form-program') {
        if (groupByFilter === 'Day') {
          const itemsArray = Object.values(nonDateBasedProgramItems);

          const currentItem = nonDateBasedProgramItems[selectedProgramDayNumber];
          const currentDayInWeek = currentItem?.dayInWeek;

          /** If it's already day 1, stop — no previous day exists */
          if (currentDayInWeek === 1) {
            return;
          }

          const prevDayInWeek = currentDayInWeek - 1;

          /** Find all items matching the previous day */
          const matchingItems = itemsArray.filter(
            (item) => item.dayInWeek === prevDayInWeek,
          );

          if (matchingItems.length === 0) {
            return;
          }
          /** Pick the one with the lowest weekNumber */
          const prevItem = matchingItems.reduce((min, item) => (item.weekNumber < min.weekNumber ? item : min));
          dispatch(setSelectedProgramDayNumber(prevItem.dayNumber));
        }
        if (groupByFilter === 'Week') {
          const currentWeekNumber = nonDateBasedProgramItems[selectedProgramDayNumber]?.weekNumber;
          const targetWeek = currentWeekNumber - 1;

          /** Get dayNumber of the first day in the previous week */
          const dayNumberOfPrevWeekStart = Object.values(nonDateBasedProgramItems).find(
            (item) => item.weekNumber === targetWeek,
          )?.dayNumber;

          if (targetWeek >= 1 && dayNumberOfPrevWeekStart !== undefined) {
            dispatch(setSelectedProgramDayNumber(dayNumberOfPrevWeekStart));
          }
        }
      }

      if (programType !== 'free-form-program') {
      /** For week view (Everyday or a weekday toggle), slide back one week */
        const prevWeekStart = moment(selectedDate)
          .subtract(7, 'days')
          .toDate();
        dispatch(setSelectedDate(prevWeekStart));
      }
    } else {
    /** For program library day view, subtract 1 day using sorted keys */
      if (programType === 'free-form-program') {
        const sortedDayNumbers = Object.keys(nonDateBasedProgramItems)
          .map(Number)
          .sort((a, b) => a - b);

        const currentIndex = sortedDayNumbers.indexOf(selectedProgramDayNumber);
        const prevSelectedProgramDayNumber = sortedDayNumbers[currentIndex - 1];

        if (prevSelectedProgramDayNumber !== undefined) {
          dispatch(setSelectedProgramDayNumber(prevSelectedProgramDayNumber));
        }
      }

      /** For calendar day view, subtract 1 day */
      if (programType !== 'free-form-program') {
        const previousDay = new Date(selectedDate);
        previousDay.setDate(previousDay.getDate() - 1);
        dispatch(setSelectedDate(previousDay));
      }
    }
  });

  /** Left/right arrow keys step the calendar the same way these buttons do. */
  useCalendarArrowNavigation({ onPrev: subtractDay, onNext: addDay });

  /** Function to set the selected date to today */
  const setToday = () => guardNavigation(() => {
    dispatch(setSelectedDate(new Date())); // Set to current date
    if (viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedWeekViewDayToggle('Everyday'));
    }
    // Recenter the rolling month window on today's week.
    if (viewType === 'monthView') {
      dispatch(initMonthViewWindow(new Date()));
    }
  });

  const nameWriter = () => {
    if (Object.keys(selectedCalendar).length) {
      return selectedCalendar?.name ? `${selectedCalendar?.name}` : 'Select Calendar';
    }
    if (Object.keys(selectedReduxAthlete).length) {
      return selectedReduxAthlete?.first && selectedReduxAthlete?.last ? `${selectedReduxAthlete?.first} ${selectedReduxAthlete?.last}` : 'Select Calendar';
    }
    return 'Select Calendar';
  };

  const programNameWriter = () => {
    if (Object.keys(selectedReduxNonDateBasedProgram).length) {
      return selectedReduxNonDateBasedProgram?.programName ? `${selectedReduxNonDateBasedProgram?.programName}` : 'Select Program';
    }
    return 'Select Program';
  };

  const calendarName = programType === 'calendar' ? nameWriter() : programNameWriter();
  const nameTextRef = useRef(null);
  const isNameTruncated = useIsTextTruncated(nameTextRef, calendarName);

  /** Re-measure whenever the calendar changes: a long name wraps the action buttons onto a
   * second row, and nothing else on the page resizes, so the resize observer below never fires
   * for it. Without this the previous calendar's header height sticks and the page and sidebar
   * stop short of the viewport bottom. Also covers the initial measurement on mount. */
  useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight;
      if (height !== headerHeight) {
        dispatch(setHeaderHeight(height));
      }
    }
  }, [calendarName, dispatch]);

  return (
    <HeaderContainer ref={headerRef} bgColor={theme.colors.background[0]} viewType={viewType}>
      <ResizeObserver
        onResize={(rect) => {
          if (rect?.height !== headerHeight) {
            dispatch(setHeaderHeight(rect?.height));
          }
        }}
      />
      <HeaderAndDropdownWrapper>
        <MobileSideBarToggle />
        <HeaderTextWrapper>
          <BreadCrumbsAndNameWrapper>
            <TabletHidden>
              <BreadCrumbs />
            </TabletHidden>
            <CalendarNameWrapper>
              <NameDiv
                role='button'
                tabIndex={0}
                onClick={() => dispatch(setActiveModal('selectCalendarModal'))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    dispatch(setActiveModal('selectCalendarModal'));
                  }
                }}
              >
                <NameText
                  ref={nameTextRef}
                  {...(isNameTruncated
                    ? {
                      'data-tooltip-id': 'calendar-name-tooltip',
                      'data-tooltip-content': calendarName,
                    }
                    : {})}
                >
                  {calendarName}
                </NameText>
                <div style={{
                  marginLeft: 4,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                >
                  <IcomoonReact iconSet={iconSet} size={10} icon='down-arrow' color='#444444' />
                </div>
                <CalendarNameTooltip id='calendar-name-tooltip' />
              </NameDiv>
            </CalendarNameWrapper>
          </BreadCrumbsAndNameWrapper>
        </HeaderTextWrapper>
      </HeaderAndDropdownWrapper>
      <MenuWrapper>
        <TabletHidden>
          <CloudUploadIcon />
        </TabletHidden>
        <NavArrowButton onClick={() => subtractDay()}>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='left-arrow'
            color='#9E9E9E'
          />
        </NavArrowButton>
        <NavArrowButton onClick={() => addDay()}>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='right-arrow'
            color='#9E9E9E'
          />
        </NavArrowButton>
        {programType !== 'free-form-program' ? (
          <TodayButton onClick={setToday}>
            <Text fontSize='16px'>Today</Text>
          </TodayButton>
        ) : null}
        <DaySelectBox onClick={() => setShowDropdown(!showDropdown)}>
          <HeaderDropdownMenu />
        </DaySelectBox>
        <MobileHidden>
          <GearIconButtons />
        </MobileHidden>
      </MenuWrapper>
    </HeaderContainer>
  );
};

export default HeaderComponent;
